<template>
  <a-modal
      :visible="visible"
      :title="schedule ? $t('firmware_schedules.edit_title') : $t('firmware_schedules.new_title')"
      width="720px"
      :maskClosable="false"
      :closable="true"
      @cancel="onCancel"
      :footer="null">
    <a-form-model
        ref="form"
        :model="formData"
        :rules="rules"
        layout="vertical">

      <a-form-model-item :label="$t('firmware_schedules.field_name')" prop="name">
        <a-input v-model="formData.name" size="large" :placeholder="$t('firmware_schedules.field_name_placeholder')"/>
      </a-form-model-item>

      <a-form-model-item :label="$t('firmware_schedules.field_comment')" prop="comment">
        <a-textarea
            v-model="formData.comment"
            :placeholder="$t('firmware_schedules.field_comment_placeholder')"
            :autoSize="{ minRows: 2, maxRows: 4 }"
            :maxLength="1000"/>
      </a-form-model-item>

      <a-form-model-item :label="$t('firmware_schedules.field_target_type')" prop="target_type">
        <a-radio-group v-model="formData.target_type" @change="onTargetTypeChange">
          <a-radio value="organization">{{ $t('firmware_schedules.target_organization') }}</a-radio>
          <a-radio value="bases">{{ $t('firmware_schedules.target_bases') }}</a-radio>
        </a-radio-group>
      </a-form-model-item>

      <a-form-model-item
          v-if="formData.target_type === 'organization'"
          :label="$t('firmware_schedules.field_organization')"
          prop="target_organization_id">
        <searchable-organization-select
            :organizations="organizationsDropdown"
            :value="formData.target_organization_id"
            @change="(v) => formData.target_organization_id = v"
            :placeholder="$t('firmware_schedules.field_organization_placeholder')"/>
      </a-form-model-item>

      <template v-if="formData.target_type === 'bases'">
        <a-form-model-item :label="$t('firmware_schedules.field_organization_filter')">
          <searchable-organization-select
              :organizations="organizationsDropdown"
              :value="basesFilterOrgId"
              @change="onOrganizationFilterChange"
              :placeholder="$t('firmware_schedules.field_organization_filter_placeholder')"/>
        </a-form-model-item>

        <a-form-model-item :label="$t('firmware_schedules.paste_label')">
          <a-textarea
              v-model="pasteInput"
              :placeholder="$t('firmware_schedules.paste_placeholder')"
              :autoSize="{ minRows: 2, maxRows: 6 }"
              @paste.native="onPasteIntoTextarea"/>
          <div style="margin-top: 8px; display: flex; gap: 8px; align-items: center;">
            <a-button
                size="small"
                :loading="pasteLoading"
                :disabled="!pasteInput.trim()"
                @click="resolvePastedBaseIds">
              {{ $t('firmware_schedules.paste_button') }}
            </a-button>
            <small style="color: #999;">{{ $t('firmware_schedules.paste_hint') }}</small>
          </div>
          <a-alert
              v-if="pasteResult.found.length"
              type="success"
              show-icon
              style="margin-top: 8px;"
              :message="$t('firmware_schedules.paste_resolved', { count: pasteResult.found.length })"
              :description="pasteResult.foundLabels.join(', ')"/>
          <a-alert
              v-if="pasteResult.notFound.length"
              type="warning"
              show-icon
              style="margin-top: 8px;"
              :message="$t('firmware_schedules.paste_not_found', { count: pasteResult.notFound.length })"
              :description="pasteResult.notFound.join(', ')"/>
        </a-form-model-item>

        <a-form-model-item :label="$t('firmware_schedules.field_bases')" prop="base_ids">
          <a-select
              mode="multiple"
              size="large"
              :placeholder="$t('firmware_schedules.field_bases_placeholder')"
              v-model="formData.base_ids"
              @change="onBaseIdsChange"
              :filter-option="filterBaseOption">
            <a-select-option v-for="b in displayBases" :value="b.id" :key="b.id">
              {{ b.short_alias }} <small>({{ b.organization && b.organization.name ? b.organization.name + ' — ' : '' }}{{ b.long_alias || b.base_id }})</small>
            </a-select-option>
          </a-select>
        </a-form-model-item>
      </template>

      <a-alert
          v-if="overlapWarning"
          type="warning"
          show-icon
          :message="$t('firmware_schedules.overlap_warning_title')"
          style="margin-bottom: 16px;">
        <template slot="description">
          <div v-html="overlapWarning"></div>
        </template>
      </a-alert>

      <a-row :gutter="16">
        <a-col :span="8">
          <a-form-model-item :label="$t('firmware_schedules.field_firmware_version')" prop="firmware_version">
            <a-input
                v-model="formData.firmware_version"
                size="large"
                placeholder="pico_4.5.1r14.1.bin"/>
          </a-form-model-item>
        </a-col>
        <a-col :span="16">
          <a-form-model-item prop="firmware_url">
            <span slot="label">
              {{ $t('firmware_schedules.field_firmware_url') }}
              <a-tooltip v-if="firmwareUrlIsCustom" :title="$t('firmware_schedules.firmware_url_reset_tooltip')">
                <a @click="resetFirmwareUrl" style="margin-left: 8px; font-size: 12px;">
                  {{ $t('firmware_schedules.firmware_url_reset') }}
                </a>
              </a-tooltip>
            </span>
            <a-input
                v-model="formData.firmware_url"
                size="large"
                :placeholder="DEFAULT_FIRMWARE_URL_BASE + 'pico_X.Y.ZrN.bin'"/>
            <small v-if="!firmwareUrlIsCustom && formData.firmware_url" style="color: #999;">
              {{ $t('firmware_schedules.firmware_url_auto_hint') }}
            </small>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-row :gutter="16">
        <a-col :span="8">
          <a-form-model-item :label="$t('firmware_schedules.field_interval_hours')" prop="interval_hours">
            <a-select v-model="formData.interval_hours" size="large">
              <a-select-option v-for="h in allowedIntervals" :value="h" :key="h">
                {{ $t('firmware_schedules.every_x_hours', { hours: h }) }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="5">
          <a-form-model-item :label="$t('firmware_schedules.field_window_start')" prop="window_start_time">
            <a-time-picker
                :value="formData.window_start_time ? moment(formData.window_start_time, 'HH:mm') : null"
                @change="(v) => formData.window_start_time = v ? v.format('HH:mm') : null"
                size="large"
                format="HH:mm"
                style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col :span="5">
          <a-form-model-item :label="$t('firmware_schedules.field_window_end')" prop="window_end_time">
            <a-time-picker
                :value="formData.window_end_time ? moment(formData.window_end_time, 'HH:mm') : null"
                @change="(v) => formData.window_end_time = v ? v.format('HH:mm') : null"
                size="large"
                format="HH:mm"
                style="width: 100%"/>
          </a-form-model-item>
        </a-col>
        <a-col :span="6">
          <a-form-model-item :label="$t('firmware_schedules.field_timezone')" prop="timezone">
            <a-select v-model="formData.timezone" size="large" show-search>
              <a-select-option v-for="tz in timezones" :value="tz" :key="tz">{{ tz }}</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
      </a-row>

      <a-form-model-item>
        <a-checkbox v-model="formData.is_active">{{ $t('firmware_schedules.field_is_active') }}</a-checkbox>
      </a-form-model-item>

      <div style="text-align: right;">
        <a-button @click="onCancel" style="margin-right: 8px;">{{ $t('basics.cancel') }}</a-button>
        <a-button type="primary" :loading="saving" @click="onSubmit">
          {{ schedule ? $t('basics.save_changes') : $t('basics.create') }}
        </a-button>
      </div>
    </a-form-model>
  </a-modal>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import SearchableOrganizationSelect from '@/components/SearchableOrganizationSelect';
import { getBasesList } from '@/api/bases';
import { requiredField } from '@/utils/messages';

const ALLOWED_INTERVALS = [1, 2, 3, 4, 6, 8, 12, 24];

const DEFAULT_FIRMWARE_URL_BASE = 'https://update.osol.fr/pico_firmwares/';

function buildAutoFirmwareUrl(version) {
  return version ? DEFAULT_FIRMWARE_URL_BASE + version : '';
}

const TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Berlin',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Tokyo'
];

const emptyForm = () => ({
  name: '',
  target_type: 'organization',
  target_organization_id: null,
  base_ids: [],
  firmware_url: '',
  firmware_version: '',
  interval_hours: 6,
  window_start_time: '02:00',
  window_end_time: '06:00',
  timezone: 'Europe/Paris',
  comment: '',
  is_active: false
});

export default {
  name: 'FirmwareScheduleForm',
  components: { SearchableOrganizationSelect },
  props: {
    visible: { type: Boolean, default: false },
    schedule: { type: Object, default: null }
  },
  data() {
    return {
      moment,
      saving: false,
      formData: emptyForm(),
      basesFilterOrgId: null,
      availableBases: [],
      extraBases: [],
      pasteInput: '',
      pasteLoading: false,
      pasteResult: { found: [], foundLabels: [], notFound: [] },
      overlapWarning: '',
      allowedIntervals: ALLOWED_INTERVALS,
      timezones: TIMEZONES,
      DEFAULT_FIRMWARE_URL_BASE,
      rules: {
        name: [{ required: true, message: requiredField, trigger: 'blur' }],
        target_type: [{ required: true, message: requiredField }],
        target_organization_id: [{
          validator: (rule, value, cb) => {
            if (this.formData.target_type === 'organization' && !value) {
              cb(new Error(requiredField));
            } else cb();
          }
        }],
        base_ids: [{
          validator: (rule, value, cb) => {
            if (this.formData.target_type === 'bases' && (!value || !value.length)) {
              cb(new Error(requiredField));
            } else cb();
          }
        }],
        firmware_url: [
          { required: true, message: requiredField, trigger: 'blur' },
          { type: 'url', message: 'URL invalid', trigger: 'blur' }
        ],
        firmware_version: [{ required: true, message: requiredField, trigger: 'blur' }],
        interval_hours: [{ required: true, message: requiredField }],
        window_start_time: [{ required: true, message: requiredField }],
        window_end_time: [{ required: true, message: requiredField }],
        timezone: [{ required: true, message: requiredField }]
      }
    };
  },
  computed: {
    organizationsDropdown() {
      return this.$store.getters['organizations/getOrganizations'] || [];
    },
    displayBases() {
      const seen = new Set();
      const merged = [];
      [...this.availableBases, ...this.extraBases].forEach(b => {
        if (!seen.has(b.id)) {
          seen.add(b.id);
          merged.push(b);
        }
      });
      return merged;
    },
    firmwareUrlIsCustom() {
      // True if the URL doesn't match the auto-derived value for the current version
      const expected = buildAutoFirmwareUrl(this.formData.firmware_version);
      return Boolean(this.formData.firmware_url) && this.formData.firmware_url !== expected;
    }
  },
  watch: {
    visible(val) {
      if (val) this.initFromProps();
    },
    'formData.firmware_version'(newVersion, oldVersion) {
      // Auto-fill URL from version, but only if user hasn't manually overridden it
      const previousAutoUrl = buildAutoFirmwareUrl(oldVersion);
      const isUrlAuto = !this.formData.firmware_url || this.formData.firmware_url === previousAutoUrl;
      if (isUrlAuto) {
        this.formData.firmware_url = buildAutoFirmwareUrl(newVersion);
      }
    }
  },
  mounted() {
    if (!this.organizationsDropdown.length) {
      this.$store.dispatch('organizations/GetOrganizationDropdownList');
    }
    if (this.visible) this.initFromProps();
  },
  methods: {
    initFromProps() {
      this.pasteInput = '';
      this.pasteResult = { found: [], foundLabels: [], notFound: [] };
      this.extraBases = [];
      if (this.schedule) {
        this.formData = {
          name: this.schedule.name,
          target_type: this.schedule.target_type,
          target_organization_id: this.schedule.target_organization_id || null,
          base_ids: this.schedule.base_ids || [],
          firmware_url: this.schedule.firmware_url,
          firmware_version: this.schedule.firmware_version,
          interval_hours: this.schedule.interval_hours,
          window_start_time: this.schedule.window_start_time,
          window_end_time: this.schedule.window_end_time,
          timezone: this.schedule.timezone || 'Europe/Paris',
          comment: this.schedule.comment || '',
          is_active: !!this.schedule.is_active
        };
        if (this.schedule.target_type === 'bases' && this.schedule.bases && this.schedule.bases.length) {
          this.basesFilterOrgId = this.schedule.bases[0].organization_id || null;
          // Pre-populate extraBases so the multi-select shows labels for all selected bases,
          // even if they belong to organizations other than the filter one.
          this.extraBases = this.schedule.bases.map(b => ({
            id: b.id,
            base_id: b.base_id,
            short_alias: b.short_alias,
            long_alias: b.long_alias,
            organization: b.organization || null
          }));
        }
      } else {
        this.formData = emptyForm();
        this.basesFilterOrgId = null;
        this.availableBases = [];
      }
      this.overlapWarning = '';
      if (this.formData.target_type === 'bases' && this.basesFilterOrgId) {
        this.loadBasesForOrg(this.basesFilterOrgId);
      }
    },
    onTargetTypeChange() {
      this.formData.base_ids = [];
      this.formData.target_organization_id = null;
      this.basesFilterOrgId = null;
      this.availableBases = [];
      this.extraBases = [];
      this.pasteInput = '';
      this.pasteResult = { found: [], foundLabels: [], notFound: [] };
      this.overlapWarning = '';
    },
    onOrganizationFilterChange(value) {
      this.basesFilterOrgId = value;
      this.loadBasesForOrg(value);
    },
    resetFirmwareUrl() {
      this.formData.firmware_url = buildAutoFirmwareUrl(this.formData.firmware_version);
    },
    onPasteIntoTextarea() {
      // Auto-trigger detection ~150ms after paste so user doesn't even need to click the button
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.pasteInput.trim()) this.resolvePastedBaseIds();
        }, 150);
      });
    },
    parsePastedInput(raw) {
      if (!raw) return [];
      // Remove enclosing JSON-array brackets if present
      let cleaned = raw.replace(/^\s*\[/, '').replace(/\]\s*$/, '');
      // Split on quotes, commas, semicolons, whitespace, newlines
      const tokens = cleaned.split(/["',;\s\n\r\t]+/);
      return [...new Set(tokens.map(t => t.trim()).filter(t => t.length > 0))];
    },
    resolvePastedBaseIds() {
      const ids = this.parsePastedInput(this.pasteInput);
      if (!ids.length) {
        this.pasteResult = { found: [], foundLabels: [], notFound: [] };
        return;
      }
      this.pasteLoading = true;
      this.$store.dispatch('firmwareSchedules/ResolveBaseIds', ids)
          .then(response => {
            const found = response.found || [];
            const notFound = response.not_found || [];
            // Merge into extraBases (dedup by id)
            const newExtras = [...this.extraBases];
            found.forEach(b => {
              if (!newExtras.find(e => e.id === b.id)) newExtras.push(b);
            });
            this.extraBases = newExtras;
            // Add resolved IDs to selection (dedup)
            const newSelection = [...this.formData.base_ids];
            found.forEach(b => {
              if (!newSelection.includes(b.id)) newSelection.push(b.id);
            });
            this.formData.base_ids = newSelection;
            // Display feedback
            this.pasteResult = {
              found,
              foundLabels: found.map(b => `${b.short_alias} (${b.organization && b.organization.name ? b.organization.name : '—'})`),
              notFound
            };
            // Trigger overlap check
            this.onBaseIdsChange(this.formData.base_ids);
          })
          .catch(error => {
            const msg = error?.response?.data?.message || this.$t('firmware_schedules.paste_error');
            this.$message.error(msg);
          })
          .finally(() => { this.pasteLoading = false; });
    },
    loadBasesForOrg(orgId) {
      if (!orgId) {
        this.availableBases = [];
        return;
      }
      getBasesList(orgId)
          .then(response => { this.availableBases = response.data || []; })
          .catch(() => { this.availableBases = []; });
    },
    onBaseIdsChange: _.debounce(function (ids) {
      if (!ids || !ids.length) {
        this.overlapWarning = '';
        return;
      }
      this.$store.dispatch('firmwareSchedules/CheckOverlaps', {
        baseIds: ids,
        excludeScheduleId: this.schedule ? this.schedule.id : null
      }).then(response => {
        const overlaps = response.overlaps || {};
        const items = [];
        Object.keys(overlaps).forEach(baseId => {
          const base = this.availableBases.find(b => b.id == baseId);
          const baseLabel = base ? base.short_alias : `base #${baseId}`;
          const scheduleLabels = overlaps[baseId].map(s => `<b>${s.name}</b>`).join(', ');
          items.push(`<li>${baseLabel} → ${scheduleLabels}</li>`);
        });
        this.overlapWarning = items.length
            ? `<ul style="margin: 0; padding-left: 18px;">${items.join('')}</ul>`
            : '';
      });
    }, 400),
    filterBaseOption(input, option) {
      const text = option.componentOptions?.children?.[0]?.text || '';
      return text.toLowerCase().includes(input.toLowerCase());
    },
    onCancel() {
      this.$emit('close');
    },
    onSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return;

        this.saving = true;
        const payload = { ...this.formData };
        if (payload.target_type === 'organization') {
          delete payload.base_ids;
        } else {
          payload.target_organization_id = null;
        }

        const action = this.schedule
            ? this.$store.dispatch('firmwareSchedules/Update', { id: this.schedule.id, data: payload })
            : this.$store.dispatch('firmwareSchedules/Create', payload);

        action
            .then(() => {
              this.$message.success(this.schedule
                  ? this.$t('firmware_schedules.update_success')
                  : this.$t('firmware_schedules.create_success'));
              this.$emit('saved');
            })
            .catch(error => {
              const msg = error?.response?.data?.message
                  || (this.schedule
                      ? this.$t('firmware_schedules.update_failure')
                      : this.$t('firmware_schedules.create_failure'));
              this.$message.error(msg);
            })
            .finally(() => { this.saving = false; });
      });
    }
  }
};
</script>
